import { Injectable, Injector, NgZone } from '@angular/core';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, forkJoin, take } from 'rxjs';
import { UiOverlayController, UiOverlayService } from '@cynteka/ui-kit';
import { GoodBaseBatchDialogComponent, GoodBaseOption, GoodBasePositionInput } from './goodbase-batch-dialog.component';
import { GOODBASE_FORM } from '../../cyn/fck-chat-module/core/forms/ai-form-catalog';
import { isAiFormPage } from '../../cyn/fck-chat-module/core/forms/ai-form-values';

@Injectable()
class GoodBaseOverlayContainer extends OverlayContainer {
    protected _createContainer(): void {
        super._createContainer();
        this._containerElement.classList.add('goodbase-overlay-container');
    }
}

@Injectable({ providedIn: 'root' })
export class GoodBaseBatchService {
    private overlay: UiOverlayController<GoodBaseBatchDialogComponent> | null = null;
    private opening: Promise<GoodBaseBatchDialogComponent> | null = null;
    private readonly overlays: UiOverlayService;

    constructor(private readonly http: HttpClient, injector: Injector, private readonly zone: NgZone) {
        // Форма ниже чата, а меню самого чата остаются в общем overlay-контейнере выше него.
        this.overlays = new UiOverlayService(Injector.create({ parent: injector, providers: [
            { provide: Overlay, useClass: Overlay }, { provide: OverlayContainer, useClass: GoodBaseOverlayContainer },
        ] }));
    }

    open(rows: unknown[] = [], addInitialRow = true): Promise<GoodBaseBatchDialogComponent> {
        if (!isAiFormPage(GOODBASE_FORM)) return Promise.reject(new Error('Форма доступна только на странице товарной базы'));
        if (this.overlay) return Promise.resolve(this.overlay.getOverlayAttachComponentRef());
        if (this.opening) return this.opening;
        if (!Array.isArray(rows) || rows.length > 100) return Promise.reject(new Error('Ожидается массив до 100 строк'));
        this.opening = this.openDialog(rows, addInitialRow).finally(() => { this.opening = null; });
        return this.opening;
    }

    private async openDialog(rows: unknown[], addInitialRow: boolean): Promise<GoodBaseBatchDialogComponent> {
        const data = await firstValueFrom(forkJoin({
            permissions: this.http.get<Record<string, boolean>>('/core/users/getCurrentUserPermissions/json'),
            creator: this.http.get<{ id: number }>('/core/users/getCurrentUser/json'),
            categories: this.http.get<GoodBaseOption[]>('/core/categories/json', { params: { onlyAssignable: 'true' } }),
            units: this.http.get<GoodBaseOption[]>('/core/goodbase/units/json'),
        }));
        if (!data.permissions.GOOD_BASE_ITEM_CREATE) throw new Error('Нет права добавлять товары');
        if (!data.creator?.id || !Array.isArray(data.categories) || !Array.isArray(data.units)) throw new Error('Не удалось загрузить данные формы');
        if (!isAiFormPage(GOODBASE_FORM)) throw new Error('Страница товарной базы уже закрыта');
        return this.zone.run(() => {
            let component: GoodBaseBatchDialogComponent;
            const overlay = this.overlays.createOverlay(GoodBaseBatchDialogComponent, {
                position: { global: { x: 'center', y: 'center' } },
                size: { width: '1000px', maxWidth: '95vw', maxHeight: '90vh' },
                hasBackdrop: true, backdropClass: 'ui-bg-bg-overlay-default', scrollStrategy: 'block',
                autoCloseOnEscape: true, autoCloseOnOutside: false, autoCloseOnlyIfLast: true,
                autoCloseCallback: (_event, { onEscape }) => onEscape && !component?.saving,
            });
            const session = overlay.open({
                data: { categories: data.categories, units: data.units, creator: { id: data.creator.id }, initialRows: rows, addInitialRow },
                savePosition: position => this.createPosition(position),
                close: () => overlay.close(),
                onSaved: () => document.dispatchEvent(new CustomEvent('cynteka-goodbase-changed')),
            });
            component = overlay.getOverlayAttachComponentRef();
            this.overlay = overlay;
            const closeOnNavigation = () => { if (!isAiFormPage(GOODBASE_FORM)) overlay.close(); };
            window.addEventListener('hashchange', closeOnNavigation);
            session.afterClosing.pipe(take(1)).subscribe(() => {
                window.removeEventListener('hashchange', closeOnNavigation);
                this.overlay = null;
                overlay.destroy();
            });
            return component;
        });
    }

    private createPosition(position: GoodBasePositionInput): Promise<{ id: number }> {
        return firstValueFrom(this.http.post<{ id: number }>('/core/goodbase/createposition', position));
    }
}
